import React from "react";
import { Box, Container, Typography, Button, Icon } from "@mui/material";
import { useTheme } from "@emotion/react";
import { Link as RouterLink } from "react-router-dom";
import WaitlistCTA from "./WaitlistCTA.js";

/**
 * Two-tier pricing section for teachers and for schools/districts. Reflects
 * Maestro's grant-funded model: teachers never pay directly; schools and
 * districts partner around scope and funding availability.
 */
const Pricing = () => {
  const theme = useTheme();

  const tiers = [
    {
      audience: "Teachers",
      icon: "person",
      price: "$0",
      priceSub: "for teachers, always",
      body: "Join through your school or district. Skill build, weekly coach, and every material delivered. No credit card, no monthly fee.",
      bullets: [
        "Personalized AI skill built for your workflow",
        "Weekly coaching call to iterate the skill",
        "All materials delivered to your Drive",
      ],
      cta: "Join the teacher waitlist",
      to: "/waitlist",
    },
    {
      audience: "Schools & districts",
      icon: "domain",
      price: "Grant-funded",
      priceSub: "partnerships",
      body: "We partner with schools and districts on grant-funded pilots, with scope and structure tailored to what your teachers need and what funding is available.",
      bullets: [
        "Typical pilot: 5-25 STEM teachers per year",
        "Coach roster + skill library built with your team",
        "District-level dashboards + year-end report",
      ],
      cta: "Talk to us about a pilot",
      to: "/waitlist",
      featured: true,
    },
  ];

  return (
    <Box
      component="section"
      id="pricing"
      sx={{
        py: { xs: 8, md: 14 },
        bgcolor: theme.palette.paper.primary,
      }}
    >
      <Container maxWidth="lg">
        <Typography
          sx={{
            display: "inline-block",
            fontSize: 12,
            letterSpacing: "0.14em",
            textTransform: "uppercase",
            color: theme.palette.success.main,
            fontWeight: 700,
            mb: 2.5,
          }}
        >
          Pricing
        </Typography>
        <Typography
          component="h2"
          sx={{
            fontSize: { xs: "2rem", md: "3.25rem" },
            fontWeight: 800,
            letterSpacing: "-0.03em",
            lineHeight: 1.06,
            color: theme.palette.text.primary,
          }}
        >
          Grant-funded.{" "}
          <Box component="span" sx={{ color: theme.palette.success.main }}>
            Teacher-first
          </Box>
          .
        </Typography>
        <Typography
          sx={{
            fontSize: "1.25rem",
            color: theme.palette.text.secondary,
            mt: 2.75,
            maxWidth: 640,
            lineHeight: 1.5,
          }}
        >
          Maestro is built around a simple rule: teachers never pay to use it.
          Everything is funded through grants and school / district
          partnerships.
        </Typography>

        <Box
          sx={{
            mt: 8,
            display: "grid",
            gridTemplateColumns: { xs: "1fr", md: "1fr 1fr" },
            gap: { xs: 12, md: 3.5 },
            alignItems: "stretch",
          }}
        >
          {tiers.map((tier) => (
            <Box
              key={tier.audience}
              sx={{
                position: "relative",
                height: "100%",
                bgcolor: tier.featured
                  ? theme.palette.text.primary
                  : theme.palette.paper.primary,
                color: tier.featured
                  ? theme.palette.paper.primary
                  : theme.palette.text.primary,
                border: `1px solid ${
                  tier.featured
                    ? theme.palette.text.primary
                    : theme.palette.text.tertiary
                }`,
                borderRadius: 3,
                p: { xs: 4, md: 5 },
                display: "flex",
                flexDirection: "column",
                gap: 2.5,
                transition: "transform 0.15s, box-shadow 0.15s",
                "&:hover": {
                  transform: "translateY(-4px)",
                  boxShadow: tier.featured
                    ? "0 20px 40px rgba(0,0,0,0.35)"
                    : "0 12px 32px rgba(0,0,0,0.08)",
                },
              }}
            >
              {tier.featured && (
                <Box
                  sx={{
                    position: "absolute",
                    top: -12,
                    right: 32,
                    bgcolor: theme.palette.success.main,
                    color: theme.palette.paper.primary,
                    fontSize: 11,
                    fontWeight: 700,
                    letterSpacing: "0.1em",
                    textTransform: "uppercase",
                    px: 1.5,
                    py: 0.75,
                    borderRadius: 999,
                  }}
                >
                  Most partnerships
                </Box>
              )}

              <Box
                sx={{
                  display: "flex",
                  alignItems: "center",
                  gap: 1.5,
                }}
              >
                <Icon
                  baseClassName="material-symbols-outlined"
                  sx={{
                    fontSize: 28,
                    color: tier.featured
                      ? theme.palette.success.main
                      : theme.palette.text.primary,
                  }}
                >
                  {tier.icon}
                </Icon>
                <Typography
                  sx={{
                    fontSize: "0.9375rem",
                    fontWeight: 700,
                    letterSpacing: "0.02em",
                  }}
                >
                  {tier.audience}
                </Typography>
              </Box>

              <Box>
                <Typography
                  sx={{
                    fontSize: { xs: "2.5rem", md: "3rem" },
                    fontWeight: 800,
                    letterSpacing: "-0.03em",
                    lineHeight: 1,
                    color: tier.featured
                      ? theme.palette.success.main
                      : theme.palette.text.primary,
                  }}
                >
                  {tier.price}
                </Typography>
                <Typography
                  sx={{
                    fontSize: "0.9375rem",
                    color: tier.featured
                      ? "rgba(255,255,255,0.6)"
                      : theme.palette.text.secondary,
                    mt: 0.75,
                  }}
                >
                  {tier.priceSub}
                </Typography>
              </Box>

              <Typography
                sx={{
                  fontSize: "1rem",
                  color: tier.featured
                    ? "rgba(255,255,255,0.8)"
                    : theme.palette.text.secondary,
                  lineHeight: 1.55,
                }}
              >
                {tier.body}
              </Typography>

              <Box
                component="ul"
                sx={{
                  m: 0,
                  p: 0,
                  listStyle: "none",
                  display: "flex",
                  flexDirection: "column",
                  gap: 1.25,
                }}
              >
                {tier.bullets.map((b) => (
                  <Box
                    key={b}
                    component="li"
                    sx={{
                      display: "flex",
                      alignItems: "flex-start",
                      gap: 1.25,
                      fontSize: "0.9375rem",
                      lineHeight: 1.5,
                      color: tier.featured
                        ? "rgba(255,255,255,0.85)"
                        : theme.palette.text.primary,
                    }}
                  >
                    <Icon
                      baseClassName="material-symbols-outlined"
                      sx={{
                        fontSize: 20,
                        color: theme.palette.success.main,
                        flexShrink: 0,
                        mt: 0.25,
                      }}
                    >
                      check_circle
                    </Icon>
                    {b}
                  </Box>
                ))}
              </Box>

              <Button
                component={RouterLink}
                to={tier.to}
                sx={{
                  mt: "auto",
                  bgcolor: tier.featured
                    ? theme.palette.success.main
                    : theme.palette.text.primary,
                  color: theme.palette.paper.primary,
                  px: { xs: 4, md: 5 },
                  py: { xs: 1.75, md: 2 },
                  fontSize: { xs: "0.9375rem", md: "1rem" },
                  fontWeight: 700,
                  textTransform: "none",
                  borderRadius: 999,
                  "&:hover": {
                    bgcolor: tier.featured ? "#15803d" : "#1a1a1a",
                    transform: "translateY(-2px)",
                  },
                  transition: "transform 0.15s, background-color 0.15s",
                }}
              >
                {tier.cta} →
              </Button>
            </Box>
          ))}
        </Box>
      </Container>
    </Box>
  );
};

export default Pricing;
